import { CARGO_KINDS, LOOT_CARGO_KINDS, type CargoKind } from "./risk.js";

export const CARGO_STACK_LIMIT = 5;
// Keep this ceiling when reducing the live catalog: saved flights/losses must remain readable.
export const MAX_CARGO_SLOTS = 64;
export const MAX_CARGO_UNITS = MAX_CARGO_SLOTS * CARGO_STACK_LIMIT;
export interface CargoGroup {
  kind: CargoKind;
  count: number;
  slots: number;
}
export interface CargoStack {
  kind: CargoKind;
  count: number;
}

function cargoCounts(kinds: readonly CargoKind[]): Record<CargoKind, number> {
  const counts: Record<CargoKind, number> = {
    energy: 0,
    salvage: 0,
    contraband: 0,
    battery: 0,
    rocket: 0,
    drone: 0,
  };
  for (const kind of kinds) counts[kind]++;
  return counts;
}

/** Counts are projections only; physical items retain their IDs, sources and reward terms. */
export function cargoGroups(kinds: readonly CargoKind[]): CargoGroup[] {
  const counts = cargoCounts(kinds);
  return CARGO_KINDS.flatMap((kind) =>
    counts[kind] === 0
      ? []
      : [
          {
            kind,
            count: counts[kind],
            slots: Math.ceil(counts[kind] / CARGO_STACK_LIMIT),
          },
        ],
  );
}

export function cargoStacks(kinds: readonly CargoKind[]): CargoStack[] {
  return cargoGroups(kinds).flatMap((group) =>
    Array.from({ length: group.slots }, (_, index) => ({
      kind: group.kind,
      count: Math.min(
        CARGO_STACK_LIMIT,
        group.count - index * CARGO_STACK_LIMIT,
      ),
    })),
  );
}

export function cargoUsedSlots(kinds: readonly CargoKind[]): number {
  return cargoGroups(kinds).reduce((slots, group) => slots + group.slots, 0);
}

export function canFitCargo(
  kinds: readonly CargoKind[],
  capacity: number,
): boolean {
  return cargoUsedSlots(kinds) <= capacity;
}

export function canAddCargo(
  kinds: readonly CargoKind[],
  kind: CargoKind,
  capacity: number,
): boolean {
  return canFitCargo([...kinds, kind], capacity);
}

export function canCollectCargo(
  kinds: readonly CargoKind[],
  capacity: number,
  kind: CargoKind | null,
): boolean {
  return kind === null
    ? LOOT_CARGO_KINDS.some((candidate) => canAddCargo(kinds, candidate, capacity))
    : canAddCargo(kinds, kind, capacity);
}
